import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle, Clock, User, Building2, AlertCircle, Calendar, MessageSquare, Edit2, Save, XCircle } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface Comment {
  id: string;
  text: string;
  author: string;
  authorEmail: string;
  isInternal: boolean;
  createdAt: string;
}

interface Ticket {
  id: string;
  firmId: string;
  firmName: string;
  requestType: string;
  summary: string;
  description: string;
  status: string;
  priority: string;
  impact: string;
  urgency: string;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
  jiraUrl?: string | null;
  comments?: Comment[];
}

interface TicketDetailModalProps {
  ticket: Ticket;
  onClose: () => void;
  onUpdate: () => void;
  companyId?: string;
  accessToken?: string;
}

export function TicketDetailModal({ ticket, onClose, onUpdate, companyId, accessToken }: TicketDetailModalProps) {
  const [activeTicket, setActiveTicket] = useState<Ticket>(ticket);
  const [newComment, setNewComment] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [isInternal, setIsInternal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editedCommentText, setEditedCommentText] = useState('');

  const statusOptions = ['Open', 'In Progress', 'Waiting for Customer', 'Resolved', 'Closed'];
  const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${activeTicket.firmId}/${activeTicket.id}/comment`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            comment: newComment,
            isInternal,
            author: 'System Admin',
            authorEmail: 'admin@pathkit.pro',
            mentions: mentionedUsers,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[TICKET MODAL] Server error:', errorData);
        throw new Error(errorData.error || 'Failed to add comment');
      }

      const data = await response.json();
      setActiveTicket(data.ticket);
      setNewComment('');
      setMentionedUsers([]);
      setIsInternal(false);
      onUpdate();
    } catch (error: any) {
      console.error('[TICKET MODAL] Error adding comment:', error);
      alert(`Failed to add comment: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditedCommentText(currentText);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editedCommentText.trim()) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${activeTicket.firmId}/${activeTicket.id}/comment/${commentId}`,
        {
          method: 'PATCH',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: editedCommentText,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[TICKET MODAL] Error updating comment:', errorData);
        throw new Error(errorData.error || 'Failed to update comment');
      }

      const data = await response.json();
      setActiveTicket(data.ticket);
      setEditingCommentId(null);
      setEditedCommentText('');
      onUpdate();
    } catch (error: any) {
      console.error('[TICKET MODAL] Error updating comment:', error);
      alert(`Failed to update comment: ${error.message}`);
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${activeTicket.firmId}/${activeTicket.id}/status`,
        {
          method: 'PATCH',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[TICKET MODAL] Server error:', errorData);
        throw new Error(errorData.error || 'Failed to update status');
      }

      const data = await response.json();
      setActiveTicket(data.ticket);
      onUpdate();
    } catch (error: any) {
      console.error('[TICKET MODAL] Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleUpdatePriority = async (newPriority: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${activeTicket.firmId}/${activeTicket.id}/priority`,
        {
          method: 'PATCH',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            priority: newPriority,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[TICKET MODAL] Server error:', errorData);
        throw new Error(errorData.error || 'Failed to update priority');
      }

      const data = await response.json();
      setActiveTicket(data.ticket);
      onUpdate();
    } catch (error: any) {
      console.error('[TICKET MODAL] Error updating priority:', error);
      alert(`Failed to update priority: ${error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'waiting for customer':
        return 'bg-purple-100 text-purple-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl">{activeTicket.id}</h2>
              <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(activeTicket.status)}`}>
                {activeTicket.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs ${getPriorityColor(activeTicket.priority)}`}>
                {activeTicket.priority} Priority
              </span>
            </div>
            <h3 className="text-xl mb-2">{activeTicket.summary}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {activeTicket.firmName}
              </div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {activeTicket.createdByName || activeTicket.createdBy}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(activeTicket.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2 text-gray-600">Update Status</label>
              <select
                value={activeTicket.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                disabled={isUpdatingStatus}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2 text-gray-600">Update Priority</label>
              <select
                value={activeTicket.priority}
                onChange={(e) => handleUpdatePriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {priorityOptions.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="mb-2 text-gray-600">Description</h4>
            <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">
              {activeTicket.description}
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Request Type</div>
              <div>{activeTicket.requestType}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Impact</div>
              <div>{activeTicket.impact}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Urgency</div>
              <div>{activeTicket.urgency}</div>
            </div>
          </div>

          {/* Comments Section */}
          <div>
            <h4 className="mb-3 flex items-center gap-2 text-gray-600">
              <MessageSquare className="h-5 w-5" />
              Comments & Responses
            </h4>
            
            {activeTicket.comments && activeTicket.comments.length > 0 ? (
              <div className="space-y-3 mb-4">
                {activeTicket.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`rounded-lg p-4 ${
                      comment.isInternal 
                        ? 'bg-yellow-50 border-2 border-yellow-300' 
                        : 'bg-blue-50 border-2 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{comment.author}</span>
                        {comment.isInternal ? (
                          <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs rounded font-medium">
                            🔒 Internal Note
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-blue-400 text-blue-900 text-xs rounded font-medium">
                            👁️ Client-Facing
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {new Date(comment.createdAt).toLocaleString()}
                        </span>
                        {activeTicket.id.startsWith('KV-') && editingCommentId !== comment.id && (
                          <button
                            onClick={() => handleEditComment(comment.id, comment.text)}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Edit comment"
                          >
                            <Edit2 className="h-3 w-3 text-gray-600" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingCommentId === comment.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editedCommentText}
                          onChange={(e) => setEditedCommentText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(comment.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 text-sm"
                          >
                            <Save className="h-3 w-3" />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center gap-1 text-sm"
                          >
                            <XCircle className="h-3 w-3" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">
                        {comment.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 mb-4">
                No comments yet
              </div>
            )}

            {/* Add Comment */}
            <div className="border-t border-gray-200 pt-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment or response..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
              />
              <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-gray-600">Internal note (not visible to consultant)</span>
                </label>
                <button
                  onClick={handleAddComment}
                  disabled={isSubmitting || !newComment.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Response
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}